"use client"

import Link from "next/link"
import { cn } from "@/lib/utils"

interface OpenInChimeStudioProps {
  /** Library sound ID. When provided, links to /convert?sound=<id>; omit to link to /convert directly. */
  soundId?: string
  className?: string
  /** Visual variant — 'subtle' renders as a small inline text link; 'button' renders as a pill button */
  variant?: "subtle" | "button"
}

/**
 * "Open in Chime Studio" entry link.
 * When soundId is provided, navigates to /convert?sound=<id> so the editor prefills with the library sound.
 * Without soundId, navigates to /convert as a general CTA.
 */
export function OpenInChimeStudio({
  soundId,
  className,
  variant = "subtle",
}: OpenInChimeStudioProps) {
  const href = soundId ? `/convert?sound=${encodeURIComponent(soundId)}` : '/convert'

  if (variant === "button") {
    return (
      <Link
        href={href}
        className={cn(
          "tlsr-focus inline-flex items-center gap-2 rounded-[var(--radius-pill)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface-elevated)] px-3 py-2 text-xs font-medium text-[var(--tls-fg-muted)] transition-[border-color,background-color,color] duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)] hover:border-[color:var(--tls-border-strong)] hover:bg-[var(--tls-surface-hover)] hover:text-[var(--tls-fg-strong)]",
          className
        )}
        aria-label="Open in Chime Studio to adjust loudness and fades"
      >
        Open in Chime Studio
      </Link>
    )
  }

  return (
    <Link
      href={href}
      className={cn(
        "tlsr-focus inline-flex items-center gap-1.5 text-xs text-[var(--tls-fg-subtle)] underline-offset-2 hover:text-[var(--tls-fg-muted)] hover:underline transition-colors duration-[var(--motion-duration-standard)]",
        className
      )}
      aria-label="Open in Chime Studio to adjust loudness and fades"
    >
      Open in Chime Studio
    </Link>
  )
}
